import React, { useEffect, useState } from 'react';
import { useTranslate } from '../hooks/useTranslate';
import { useAuth } from '../contexts/AuthContext';
import LanguageSelectorModal from '../components/LanguageSelectorModal';
import { IoLanguage } from 'react-icons/io5';

/*
  EntryPage: Landing/intro page with glassmorphic hero card.
  - Frosted glass card on background image
  - Logo, tagline (dark text), language selector, and Get Started button
  - Language selector opens modal overlay
  - Futuristic design with clean animations
*/

const EntryPage: React.FC = () => {
    const { login } = useAuth();
    const { t } = useTranslate();
    const [cardVisible, setCardVisible] = useState(false);
    const [showLanguageModal, setShowLanguageModal] = useState(false);

    const prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia
        ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
        : false;

    useEffect(() => {
        if (prefersReducedMotion) {
            setCardVisible(true);
            return;
        }
        const t = setTimeout(() => setCardVisible(true), 200);
        return () => clearTimeout(t);
    }, [prefersReducedMotion]);

    return (
        <div
            className="zoom-reset relative overflow-hidden"
            style={{
                backgroundImage: 'url("/Login Page.png")',
                backgroundSize: '100% 100%',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                position: 'fixed',
                top: 0,
                left: 0,
            }}
        >
            {/* Rich's Logo — top left */}
            <div className="absolute top-6 left-8 z-20 flex items-center h-16">
                <img
                    src="/RICH White Logo-01 1.svg"
                    alt="Rich's Logo"
                    className="h-16 w-auto object-contain"
                />
            </div>

            {/* Data Doctor Logo — top right */}
            <div className="absolute top-6 right-8 z-20 flex items-center h-28">
                <img
                    src="/new_logo-removebg-preview.svg"
                    alt="Data Doctor Logo"
                    className="h-28 w-auto object-contain"
                    style={{
                        filter: 'drop-shadow(0 2px 8px rgba(155, 5, 39, 0.1))',
                    }}
                />
            </div>

            {/* Center content */}
            <main className="relative z-10 flex items-center justify-center min-h-screen px-4">
                <div
                    className="w-full max-w-4xl"
                    style={{
                        opacity: cardVisible ? 1 : 0,
                        transform: cardVisible ? 'translateY(0) scale(1)' : 'translateY(24px) scale(0.97)',
                        transition: 'opacity 0.7s cubic-bezier(0.16, 1, 0.3, 1), transform 0.7s cubic-bezier(0.16, 1, 0.3, 1)',
                    }}
                    data-testid="hero-content"
                >
                    {/* Card Container */}
                    <div
                        className="relative"
                    >
                        <div className="px-8 pt-10 pb-8 flex flex-col items-center text-center">

                            {/* Tagline — dark text on frosted glass */}
                            <h1
                                className="text-xl sm:text-2xl font-bold tracking-wide leading-snug mb-8"
                                style={{
                                    fontFamily: 'Verdana, Arial, sans-serif',
                                    color: '#ffffff',
                                    textShadow: '0 1px 4px rgba(0,0,0,0.3)',
                                    letterSpacing: '0.06em',
                                }}
                            >
                                {t('WHERE DATA WELLNESS AND HYGIENE MEETS INTELLIGENCE')}
                            </h1>

                            {/* Change Language Button */}
                            <button
                                onClick={() => setShowLanguageModal(true)}
                                className="group flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 hover:bg-white/20 mb-4"
                                style={{ color: '#ffffff' }}
                            >
                                <IoLanguage className="w-5 h-5 opacity-70 group-hover:opacity-100 transition-opacity" />
                                <span className="text-sm font-medium opacity-80 group-hover:opacity-100 transition-opacity">
                                    {t('Change Language')}
                                </span>
                            </button>

                            {/* Get Started Button */}
                            <button
                                onClick={login}
                                className="group relative inline-flex items-center justify-center px-10 py-3.5 rounded-xl focus:outline-none focus-visible:ring-4 focus-visible:ring-red-500/40 text-base font-semibold tracking-wide overflow-hidden transition-all duration-300 hover:shadow-lg hover:scale-[1.03] active:scale-[0.98] mb-5"
                                style={{
                                    background: '#ffffff',
                                    color: '#9B0527',
                                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
                                }}
                                aria-label="Start authentication and enter the application"
                            >
                                {/* Hover sheen */}
                                <span
                                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                                    style={{
                                        background: 'linear-gradient(105deg, transparent 40%, rgba(155,5,39,0.08) 50%, transparent 60%)',
                                    }}
                                />
                                <span className="relative z-10 flex items-center gap-2">
                                    {t('Start Your Data Checkup')}
                                    <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                        <path d="M5 12h14" />
                                        <path d="M13 6l6 6-6 6" />
                                    </svg>
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </main>

            {/* Language Modal */}
            <LanguageSelectorModal isOpen={showLanguageModal} onClose={() => setShowLanguageModal(false)} />
        </div>
    );
};

export default EntryPage;